import { Box, Button, Typography, useTheme } from '@mui/material';
import * as docxPreview from 'docx-preview';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TemplateType } from '../../types/template';
import { BaseModal } from '../common/UI/BaseModal';

interface TemplatePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  file: File | null;
  fileName?: string;
  templateType: TemplateType;
}

const TemplatePreviewDialog: React.FC<TemplatePreviewDialogProps> = ({
  open,
  onClose,
  file,
  fileName,
}) => {
  const theme = useTheme();
  const [error, setError] = useState<string | null>(null);

  const docxPreviewContainerRef = useRef<HTMLDivElement>(null);

  const loadPreview = useCallback(async () => {
    if (!file) {
      return;
    }
    try {
      const arrayBuffer = await file.arrayBuffer();
      if (docxPreviewContainerRef.current) {
        docxPreviewContainerRef.current.innerHTML = '';
        await docxPreview.renderAsync(arrayBuffer, docxPreviewContainerRef.current, undefined, {
          inWrapper: false,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          useBase64URL: true,
        });
      }
      setError(null);
    } catch (_err) {
      setError('Failed to generate preview. The file might be corrupted or not a valid DOCX.');
    }
  }, [file]);

  useEffect(() => {
    if (open && file) {
      loadPreview();
    } else {
      if (docxPreviewContainerRef.current) {
        docxPreviewContainerRef.current.innerHTML = '';
      }
      setError(null);
    }
  }, [open, file, loadPreview]);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', display: 'flex', flexDirection: 'column' },
      }}
      title={`Preview: ${fileName || file?.name}`}
      actions={
        <Button onClick={onClose} variant="contained" color="primary">
          Close Preview
        </Button>
      }
      contentSx={{
        p: 0,
        display: 'flex',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 4,
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: theme.palette.background.default,
        }}
      >
        {error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              '& .docx': {
                backgroundColor: 'white',
                boxShadow: theme.shadows[3],
                marginBottom: theme.spacing(4),
                minHeight: '297mm',
              },
            }}
          >
            <div
              ref={docxPreviewContainerRef}
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            />
          </Box>
        )}
      </Box>
    </BaseModal>
  );
};

export default TemplatePreviewDialog;
